const sleep = (ms, signal) => new Promise((resolve, reject) => {
  const timer = setTimeout(() => { signal?.removeEventListener('abort', abort); resolve(); }, ms);
  function abort() { clearTimeout(timer); reject(new DOMException('Cancelled', 'AbortError')); }
  if (signal?.aborted) abort();
  else signal?.addEventListener('abort', abort, { once: true });
});

export const mediaUploadError = error => {
  const code = error?.response?.data?.code || error?.code;
  return ({
    storage_full: '服务器空间不足，上传未完成。请选择较小的视频，或联系管理员扩充空间。',
    upload_busy: '视频上传任务较多，请稍后重试。',
    upload_expired: '上传记录已过期，请重新选择文件。',
    media_disabled: '此收件箱暂未启用视频上传。',
    invalid_size: '视频超过当前发送方式的大小限制。',
    invalid_filename: '文件名过长或包含特殊字符，请重命名后重新选择。',
    invalid_video: '无法识别视频内容，请检查文件是否完整。',
    offset_conflict: '上传分块发生冲突，请重新选择文件。',
    checksum_mismatch: '视频分块校验失败，请重试。',
    storage_corrupt: '视频校验失败，请重新上传。',
  })[code] || '上传暂未完成，可以重试继续上传。';
};

export function mediaUploadApi(accountId, conversationId) {
  if (!/^\d+$/.test(String(accountId)) || !/^\d+$/.test(String(conversationId))) throw new Error('Invalid upload scope');
  const root = `/api/v1/accounts/${accountId}/conversations/${conversationId}/media_uploads`;
  const idPath = id => {
    if (!/^[a-f0-9]{32}$/.test(id)) throw new Error('Invalid upload identity');
    return `${root}/${id}`;
  };
  return {
    capabilities: async () => (await window.axios.get(`${root}/capabilities`)).data,
    create: async (data, signal) => (await window.axios.post(root, data, { signal })).data,
    status: async (id, signal) => (await window.axios.get(idPath(id), { signal })).data,
    chunk: async (id, offset, sha256, data, signal) => (await window.axios.post(`${idPath(id)}/chunk`, data,
      { params: { offset, sha256 }, headers: { 'Content-Type': 'application/octet-stream' }, signal, timeout: 60000 })).data,
    complete: async (id, signal) => (await window.axios.post(`${idPath(id)}/complete`, {}, { signal })).data,
    cancel: async id => (await window.axios.delete(idPath(id))).data,
  };
}

// Each invocation captures an immutable file/scope/mode. Retry reads the server's
// committed offset rather than assuming the last HTTP response was delivered.
export async function uploadMediaChunks({ api, file, mode, requestId, uploadId, onCreated, onProgress, signal }) {
  let state = uploadId ? await api.status(uploadId, signal) : await api.create({
    filename: file.name, byte_size: file.size, content_type: file.type || 'application/octet-stream', mode, request_id: requestId,
  }, signal);
  onCreated(state.id);
  if (state.byte_size !== file.size || state.filename !== file.name || state.mode !== mode) throw new Error('Upload file changed');
  while (state.state === 'uploading' && state.received < file.size) {
    if (signal?.aborted) throw new DOMException('Cancelled', 'AbortError');
    if (!Number.isSafeInteger(state.received) || state.received < 0 || state.received > file.size ||
      !Number.isSafeInteger(state.chunk_bytes) || state.chunk_bytes <= 0 || state.chunk_bytes > 4 * 1024 * 1024) throw new Error('Invalid upload state');
    const bytes = await file.slice(state.received, state.received + state.chunk_bytes).arrayBuffer();
    const hash = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)), b => b.toString(16).padStart(2, '0')).join('');
    const expected = state.received + bytes.byteLength;
    state = await api.chunk(state.id, state.received, hash, bytes, signal);
    if (!Number.isSafeInteger(state.received) || state.received < expected || state.received > file.size) throw new Error('Invalid committed offset');
    onProgress({ stage: 'uploading', received: state.received, total: file.size });
  }
  if (state.state !== 'complete') state = await api.complete(state.id, signal);
  const deadline = Date.now() + 15 * 60 * 1000;
  while (state.state !== 'complete') {
    if (state.error) throw Object.assign(new Error('Finalization failed'), { code: state.error });
    if (!['uploading', 'sealed'].includes(state.state)) throw Object.assign(new Error('Upload failed'), { code: state.error || 'invalid_video' });
    if (Date.now() > deadline) throw new Error('Finalization pending');
    onProgress({ stage: 'verifying', received: file.size, total: file.size });
    await sleep(1500, signal);
    state = await api.status(state.id, signal);
  }
  if (!state.blob?.signed_id) throw new Error('Missing verified attachment');
  return state.blob;
}
